import { LanguageMap } from '../../../../types/LanguageMap'
import { FeatureEffectRow, FeatureEffectsChange, FeatureEffectsMap, MotivationAttributes } from '../types/Motivations'
import { motivationTypes } from './motivationOverrideHelpers'

export type FeatureEffectIdError = 'required' | 'invalid'

export type FeatureEffectValueError = 'required' | 'invalid'

export type FeatureEffectFormValues = {
  featureLegacyId: string
  choiceLegacyId: string
  effects: { type: string; value: string }[]
}

export const featureEffectStep = 0.01

export const getFeatureName = (languageMap: LanguageMap | undefined, featureLegacyId: string): string | undefined =>
  languageMap?.features?.[featureLegacyId]?.name || undefined

export const getChoiceName = (languageMap: LanguageMap | undefined, choiceLegacyId: string): string | undefined =>
  languageMap?.choices?.[choiceLegacyId]?.label || undefined

const compareText = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase())

// Rows whose feature has no EN name come last, as in legacy.
const compareRows = (a: FeatureEffectRow, b: FeatureEffectRow) =>
  Number(!a.featureName) - Number(!b.featureName) ||
  compareText(a.featureName ?? '', b.featureName ?? '') ||
  Number(a.featureLegacyId) - Number(b.featureLegacyId) ||
  compareText(a.choiceName ?? '', b.choiceName ?? '') ||
  Number(a.choiceLegacyId) - Number(b.choiceLegacyId)

export const featureEffectRowId = (row: Pick<FeatureEffectRow, 'featureLegacyId' | 'choiceLegacyId'>) => `${row.featureLegacyId}:${row.choiceLegacyId}`

export const buildFeatureEffectRows = (featureEffects: FeatureEffectsMap | undefined, languageMap: LanguageMap | undefined): FeatureEffectRow[] =>
  Object.entries(featureEffects ?? {})
    .flatMap(([featureLegacyId, choices]) =>
      Object.entries(choices ?? {}).map(([choiceLegacyId, effects]) => ({
        featureLegacyId,
        choiceLegacyId,
        featureName: getFeatureName(languageMap, featureLegacyId),
        choiceName: getChoiceName(languageMap, choiceLegacyId),
        effects,
      }))
    )
    .sort(compareRows)

export const filterFeatureEffectRows = (rows: FeatureEffectRow[], search: string): FeatureEffectRow[] => {
  const needle = search.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter((row) => (row.featureName ?? '').toLowerCase().includes(needle) || row.featureLegacyId === needle)
}

export const getFeatureEffectIdError = (id: string): FeatureEffectIdError | undefined => {
  if (!id.trim()) return 'required'
  return /^\d+$/.test(id) ? undefined : 'invalid'
}

export const isFeatureEffectPairTaken = (
  featureEffects: FeatureEffectsMap,
  featureLegacyId: string,
  choiceLegacyId: string,
  original?: Pick<FeatureEffectRow, 'featureLegacyId' | 'choiceLegacyId'>
): boolean => {
  const isOriginal = !!original && original.featureLegacyId === featureLegacyId && original.choiceLegacyId === choiceLegacyId
  return !isOriginal && !!featureEffects[featureLegacyId]?.[choiceLegacyId]
}

export const getFeatureEffectValueError = (value: string): FeatureEffectValueError | undefined => {
  if (!value.trim()) return 'required'
  return Number.isFinite(Number(value)) ? undefined : 'invalid'
}

export const toFeatureEffectFormValues = (row?: FeatureEffectRow): FeatureEffectFormValues => ({
  featureLegacyId: row?.featureLegacyId ?? '',
  choiceLegacyId: row?.choiceLegacyId ?? '',
  effects: motivationTypes.map((type) => ({ type, value: String(row?.effects[type] ?? 0) })),
})

export const toFeatureEffectRow = (values: FeatureEffectFormValues, languageMap: LanguageMap | undefined): FeatureEffectRow => ({
  featureLegacyId: values.featureLegacyId,
  choiceLegacyId: values.choiceLegacyId,
  featureName: getFeatureName(languageMap, values.featureLegacyId),
  choiceName: getChoiceName(languageMap, values.choiceLegacyId),
  effects: Object.fromEntries(values.effects.map(({ type, value }) => [type, Number(value)])),
})

const withoutRow = (featureEffects: FeatureEffectsMap, { featureLegacyId, choiceLegacyId }: FeatureEffectRow): FeatureEffectsMap => {
  if (!featureEffects[featureLegacyId]?.[choiceLegacyId]) return featureEffects
  const { [choiceLegacyId]: _removed, ...choices } = featureEffects[featureLegacyId]
  const { [featureLegacyId]: _feature, ...rest } = featureEffects
  return Object.keys(choices).length ? { ...rest, [featureLegacyId]: choices } : rest
}

// Only the exact (feature, choice) row is touched; legacy's filter also dropped rows sharing either id.
export const applyFeatureEffectsChange = (featureEffects: FeatureEffectsMap, change: FeatureEffectsChange): FeatureEffectsMap => {
  if (change.type === 'remove') return withoutRow(featureEffects, change.row)
  const base = change.original ? withoutRow(featureEffects, change.original) : featureEffects
  const { featureLegacyId, choiceLegacyId, effects } = change.row
  return { ...base, [featureLegacyId]: { ...base[featureLegacyId], [choiceLegacyId]: { ...effects } } }
}

const sameEffects = (a: MotivationAttributes, b: MotivationAttributes) => {
  const types = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...types].every((type) => a[type] === b[type])
}

export const isSameFeatureEffectsMap = (a: FeatureEffectsMap, b: FeatureEffectsMap): boolean => {
  const features = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...features].every((feature) => {
    const choicesA = a[feature]
    const choicesB = b[feature]
    if (!choicesA || !choicesB) return false
    const choices = new Set([...Object.keys(choicesA), ...Object.keys(choicesB)])
    return [...choices].every((choice) => !!choicesA[choice] && !!choicesB[choice] && sameEffects(choicesA[choice], choicesB[choice]))
  })
}
